import { Breadcrumbs as MBreadcrumbs, type FlexProps } from "@mantine/core";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useMatches, useSearchParams } from "react-router-dom";
import { type ILink } from "src/interfaces";
import { titlecase, titlecaseSentence } from "src/utils";

import { BreadcrumbContainer, BreadcrumbLink } from "./Breadcrumbs.styled";
import styles from "./Breadcrumbs.module.scss";
import { applyCustomLastLinks, collectLinks } from "./Breadcrumbs.utils";
import { useSelector } from "../../../redux/hooks";

interface Props extends FlexProps {
  children?: ReactNode;
  border?: boolean;
  customLinks?: ILink[] | ((links: ILink[]) => ILink[]);
  pb?: number;
  customLastLinks?: ILink[] | ((links: ILink[]) => ILink[]);
}

const Breadcrumbs = ({
  children,
  customLinks,
  customLastLinks,
  border = true,
  pb,
  ...rest
}: Props) => {
  const matches = useMatches();
  const { pathNames } = useSelector(state => state.preferencesSlice);
  const [searchParams] = useSearchParams();
  const [links, setLinks] = useState<ILink[]>([]);

  useEffect(() => {
    let generatedLinks = collectLinks(matches as any);

    if (customLastLinks) {
      generatedLinks = Array.isArray(customLastLinks)
        ? applyCustomLastLinks(generatedLinks, customLastLinks)
        : customLastLinks(generatedLinks);
    }

    if (customLinks) {
      if (typeof customLinks === "object" && Array.isArray(customLinks)) {
        setLinks(customLinks);
      } else {
        setLinks(customLinks(generatedLinks));
      }
    } else {
      setLinks(generatedLinks);
    }
  }, [matches, customLinks, customLastLinks]);

  const parsedLinks = useMemo(() => {
    if (Object.keys(pathNames).length === 0) return links;
    return links.map(link => {
      let matchedPath = "";
      Object.keys(pathNames).forEach(pathName => {
        if (link.href.toLowerCase().includes(pathName.toLowerCase()))
          matchedPath = pathName;
      });
      if (matchedPath) {
        return {
          ...link,
          title: link.title
            .toLowerCase()
            .replaceAll(matchedPath, pathNames[matchedPath] as string),
        };
      }
      return link;
    });
  }, [links, pathNames]);

  // Helper function to build search string for summary breadcrumbs
  const buildBreadcrumbLink = (link: ILink, isLastLink: boolean) => {
    // Preserve search params for summary links
    if (link.href.includes("/summary") && !isLastLink) {
      const queryString = searchParams.toString();
      return queryString ? `${link.href}?${queryString}` : link.href;
    }
    return link.href;
  };

  return (
    <BreadcrumbContainer
      {...rest}
      pb={pb}
      border={border}
      data-cy="breadcrumbs">
      <MBreadcrumbs h={36} separator="/" className={styles.breadcrumbsRoot}>
        {parsedLinks.map((link, i) => (
          <BreadcrumbLink
            key={`${link.href}-${i}`}
            to={buildBreadcrumbLink(link, links.length <= i + 1)}
            disabled={links.length <= i + 1}>
            {titlecaseSentence(link.title)}
          </BreadcrumbLink>
        ))}
      </MBreadcrumbs>
      {children}
    </BreadcrumbContainer>
  );
};

export default Breadcrumbs;
